<template>
  <div>

    <!-- Field changed by another user while editing -->
    <b-modal ref="field-conflict" modal-class="task-modal" size="xl" no-close-on-backdrop no-close-on-esc>
      <template #modal-header="{ close }">
        <div class="" style="width: 100%;">
          <span @click="cancelFieldConflict" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times" />
          </span>
          <h5 class="mb-1 text-center" qid="field-conflict-title">
            {{ $t('tasks.modal.field_conflict_title') }}
          </h5>
        </div>
      </template>

      <div class="form-container field-conflict" v-if="fieldConflict.field">
        <div class="field-conflict-intro">
          <font-awesome-icon icon="exclamation-triangle" class="risk-6 mr-2" />
          <span>{{ $t('tasks.modal.field_conflict_description') }}</span>
          <span class="badge field-conflict-field-badge ml-1">{{ $t(textFields()[fieldConflict.field].label) }}</span>
        </div>

        <div class="row">
          <!-- The user's own text -->
          <div class="col-lg-4 mb-3">
            <div class="field-conflict-card" :class="{ 'selected': fieldConflict.choice == 'mine' }"
              role="radio" tabindex="0" :aria-checked="fieldConflict.choice == 'mine' ? 'true' : 'false'"
              @click="fieldConflict.choice = 'mine'" @keyup.enter="fieldConflict.choice = 'mine'"
              qid="field-conflict-choice-mine">
              <div class="field-conflict-card-header">
                <span class="field-conflict-icon"><font-awesome-icon icon="user" /></span>
                <div class="field-conflict-card-title">
                  <strong>{{ $t('tasks.modal.field_conflict_mine') }}</strong>
                  <div class="text-muted small">{{ $t('tasks.modal.field_conflict_mine_hint') }}</div>
                </div>
                <font-awesome-icon icon="check-circle" class="field-conflict-check" />
              </div>
              <b-form-textarea v-if="fieldConflict.editMine" v-model="fieldConflict.mine"
                class="field-conflict-text field-conflict-textarea" qid="field-conflict-mine-textarea"></b-form-textarea>
              <div v-else class="field-conflict-text text-formatted">{{ fieldConflict.mine }}</div>
              <div class="field-conflict-card-footer">
                <span class="btn section-btn text-success cursor-pointer pl-0"
                  @click="fieldConflict.editMine = !fieldConflict.editMine">
                  <font-awesome-icon icon="pen" class="text-success" v-if="!fieldConflict.editMine" />
                  {{ fieldConflict.editMine ? $t('system.close') : $t('system.edit') }}
                </span>
              </div>
            </div>
          </div>

          <!-- The text saved by the other user -->
          <div class="col-lg-4 mb-3">
            <div class="field-conflict-card" :class="{ 'selected': fieldConflict.choice == 'theirs' }"
              role="radio" tabindex="0" :aria-checked="fieldConflict.choice == 'theirs' ? 'true' : 'false'"
              @click="fieldConflict.choice = 'theirs'" @keyup.enter="fieldConflict.choice = 'theirs'"
              qid="field-conflict-choice-theirs">
              <div class="field-conflict-card-header">
                <span class="field-conflict-icon"><font-awesome-icon icon="users" /></span>
                <div class="field-conflict-card-title">
                  <strong>{{ $t('tasks.modal.field_conflict_theirs') }}</strong>
                  <div class="text-muted small">
                    <span v-if="fieldConflict.theirsBy">{{ fieldConflict.theirsBy }}, </span>
                    {{ fieldConflict.theirsAt | formatDateTime }}
                  </div>
                </div>
                <font-awesome-icon icon="check-circle" class="field-conflict-check" />
              </div>
              <div class="field-conflict-text text-formatted">{{ fieldConflict.theirs }}</div>
              <div class="field-conflict-card-footer text-muted small">
                {{ $t('tasks.modal.field_conflict_read_only') }}
              </div>
            </div>
          </div>

          <!-- AI combination of both texts -->
          <div class="col-lg-4 mb-3">
            <div class="field-conflict-card" :class="{ 'selected': fieldConflict.choice == 'merged', 'not-generated': !fieldConflict.merged }"
              role="radio" tabindex="0" :aria-checked="fieldConflict.choice == 'merged' ? 'true' : 'false'"
              :aria-disabled="!fieldConflict.merged ? 'true' : 'false'"
              @click="fieldConflict.merged && (fieldConflict.choice = 'merged')"
              @keyup.enter="fieldConflict.merged && (fieldConflict.choice = 'merged')"
              qid="field-conflict-choice-merged">
              <div class="field-conflict-card-header">
                <span class="field-conflict-icon"><font-awesome-icon icon="sparkles" /></span>
                <div class="field-conflict-card-title">
                  <strong>{{ $t('tasks.modal.field_conflict_merged') }}</strong>
                  <div class="text-muted small">{{ $t('tasks.modal.field_conflict_merged_hint') }}</div>
                </div>
                <font-awesome-icon icon="check-circle" class="field-conflict-check" />
              </div>

              <div v-if="!fieldConflict.merged" class="field-conflict-text field-conflict-empty">
                <p class="text-muted small mb-3">{{ $t('tasks.modal.field_conflict_merged_empty') }}</p>
                <b-button variant="secondary" @click.stop="generateMergedText" :disabled="fieldConflict.mergeLoading"
                  qid="field-conflict-generate-merged">
                  <font-awesome-icon v-if="fieldConflict.mergeLoading" icon="spinner" size="sm" class="fa-spin mr-1" />
                  <font-awesome-icon v-else icon="sparkles" class="mr-1" />
                  {{ fieldConflict.mergeLoading ? $t('tasks.modal.field_conflict_generating') : $t('tasks.modal.field_conflict_generate') }}
                </b-button>
                <div v-if="fieldConflict.mergeError" class="text-danger small mt-2">{{ fieldConflict.mergeError }}</div>
              </div>
              <b-form-textarea v-else-if="fieldConflict.editMerged" v-model="fieldConflict.merged"
                class="field-conflict-text field-conflict-textarea" qid="field-conflict-merged-textarea"></b-form-textarea>
              <div v-else class="field-conflict-text text-formatted">{{ fieldConflict.merged }}</div>

              <div class="field-conflict-card-footer">
                <span class="btn section-btn text-success cursor-pointer pl-0" v-if="fieldConflict.merged"
                  @click="fieldConflict.editMerged = !fieldConflict.editMerged">
                  <font-awesome-icon icon="pen" class="text-success" v-if="!fieldConflict.editMerged" />
                  {{ fieldConflict.editMerged ? $t('system.close') : $t('system.edit') }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <template #modal-footer>
        <div class="" style="width: 100%;">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="text-right">
              <b-button variant="secondary" @click="cancelFieldConflict" qid="field-conflict-cancel-button">
                {{ $t('maintenance.measures.create.cancel') }}
              </b-button>
              <b-button class="ml-3" variant="success" @click="saveFieldConflict"
                :disabled="fieldConflict.choice == 'merged' && !fieldConflict.merged" qid="field-conflict-save-button">
                {{ $t('tasks.modal.field_conflict_save') }}
              </b-button>
            </div>
          </div>
        </div>
      </template>
    </b-modal>


    <!-- Add new link modal -->
    <b-modal ref="add-link" @hide="hideCreateLinkModal" modal-class="task-modal" hide-footer>
      <template #modal-header="{ close }">
        <div class="" style="width: 100%;">
          <span @click="hideCreateLinkModal" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times" />
          </span>
          <h5 class="mb-1 text-center" qid="update-user-title">
            {{ $t('tasks.modal.add_links_button') }}
          </h5>
        </div>
      </template>

      <b-alert v-if="error.length" show variant="danger" qid="edit-user-error">
        {{ error }}
      </b-alert>

      <div class="form-container">
        <b-form-group>
          <div>
            <label class="mt-2">{{ $t('system.link') }}</label>
            <b-form-input type="text" aria-describedby="inputGroup" v-model="inputLink"
              :placeholder="$t('maintenance.third_parties.create.links_placeholder')"
              qid="create-p-links"></b-form-input>
            <label class="mt-2">{{ $t('table.name') }}</label>
            <b-form-input type="text" aria-describedby="inputGroup" v-model="inputLinkName"
              :placeholder="inputLink ? inputLink : $t('table.name')" qid="create-p-links"></b-form-input>
          </div>
        </b-form-group>
      </div>
      <div class="proxy-footer">
        <div class="alert alert-menu-color color-secondary mb-0" role="alert">
          <div class="row">
            <div class="col-sm-6">

            </div>
            <div class="col-sm-6">
              <div class="text-right">
                <b-button class="" variant="secondary" @click="hideCreateLinkModal" qid="edit-user-cancel-button">
                  {{ $t('administration.users.create.cancel') }}
                </b-button>
                <b-button @click="handleLink" class="ml-3" variant="success" :disabled="buttonDisabled"
                  qid="edit-user-submit-button">
                  <span v-if="buttonDisabled" class="mr-1">
                    <font-awesome-icon icon="spinner" size="sm" class="fa-spin" />
                  </span>
                  {{ $t('maintenance.users.merge_users_submit') }}
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-modal>


    <!-- Change user modal template -->
    <b-modal ref="reopen-task" modal-class="task-modal">
      <template #modal-header="{ close }">
        <div class="" style="width: 100%;">
          <span @click="hideReopenDataCollectionModal" class="btn btn-secondary cursor-pointer float-right">
            <font-awesome-icon icon="times" />
          </span>
          <h5 class="mb-1 text-center">
            {{ $t('tasks.table.data_collection_assignee') }}
          </h5>
        </div>
      </template>

      <div class="form-container">

        <b-alert v-if="reopenError.length" show variant="danger" qid="create-p-error">
          {{ reopenError }}
        </b-alert>

        <b-form-group>
          <label qid="create-p-users-label">
            {{ $t('tasks.modal.recipient_label') }}
          </label>

          <div class="multiselect-row">
            <multiselect v-model="selectedAssignUsers" :options="userList" :multiple="true" :taggable="false"
              :disabled="buttonDisabled" track-by="slug" :select-label="$t('system.dropdown_select_label')"
              :selected-label="$t('system.dropdown_selected_label')"
              :deselect-label="$t('system.dropdown_deselect_label')" :placeholder="$t('tasks.modal.recipient_label')"
              label="name" qid="create-p-users-select">
              <template slot="tag" slot-scope="props">
                <span class="mr-3 user-tag">
                  <Avatar :object="props.option" size="xs"></Avatar>
                  <span class="ml-2">{{ props.option.name }}</span>
                </span>
              </template>
              <template slot="option" slot-scope="props">
                <Avatar :object="props.option" size="xs"></Avatar>
                <span class="ml-2">
                  {{ props.option.name }}
                </span>
              </template>
            </multiselect>

            <UserInviteModal :buttonType="'small'" :buttonDisabled=buttonDisabled @reloadUserList="reloadUserList">
            </UserInviteModal>
          </div>
        </b-form-group>
      </div>

      <template #modal-footer="{ close }">
        <div class="" style="width: 100%;">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="text-right">
              <b-button class="" variant="secondary" @click="hideReopenDataCollectionModal" qid="edit-p-cancel-button">
                {{ $t('maintenance.measures.create.cancel') }}
              </b-button>
              <b-button class="ml-3" variant="success" :disabled="buttonDisabled" @click="changeUsersSubmit()"
                qid="edit-p-submit-button">
                <span v-if="buttonDisabled" class="mr-1">
                  <font-awesome-icon icon="spinner" size="sm" class="fa-spin" />
                </span>
                {{ $t('maintenance.measure_categories.create.submit') }}
              </b-button>
            </div>
          </div>
        </div>
      </template>
    </b-modal>


    <AssessmentGlobalSingleTaskModalEditQuestionWizard ref="editWizard" />

<!-- Delete observation modal template -->
    <b-modal ref="delete-observation" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-observation-title">
          {{ $t('activities.system.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('activities.system.delete.description') }}
        <span v-if="itemToDelete">{{ itemToDelete.name }}</span>
      </h5>
      <h5 class="text-center">
        {{ $t('activities.system.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3" variant="secondary" @click="hideSConfirmDeleteModal"
          qid="delete-observation-cancel-button">
          {{ $t('activities.system.delete.cancel') }}
        </b-button>
        <b-button @click="deleteObservation(itemToDelete)" class="mt-3 ml-3" variant="success"
          qid="delete-observation-submit-button">
          {{ $t('activities.system.delete.submit') }}
        </b-button>
      </div>
    </b-modal>

    <!-- Delete test modal template -->
    <b-modal ref="delete-test" hide-footer>
      <div class="d-block text-center">
        <h4>
          {{ $t('activities.system.delete.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('activities.system.delete.description') }}
        <span v-if="itemToDelete">{{ itemToDelete.name }}</span>
      </h5>
      <h5 class="text-center">
        {{ $t('activities.system.delete.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3" variant="secondary" @click="hideTestConfirmDeleteModal">
          {{ $t('activities.system.delete.cancel') }}
        </b-button>
        <b-button @click="confirmDeleteTest(itemToDelete)" class="mt-3 ml-3" variant="success">
          {{ $t('activities.system.delete.submit') }}
        </b-button>
      </div>
    </b-modal>


    <!-- Edit observation modal -->
    <b-modal ref="edit-observation" hide-footer @hide="hideEditModal">
      <div class="d-block text-center">
        <h4 qid="delete-tp-title">
          {{ $t('projects.observations.update.title') }}
        </h4>
      </div>
      <b-alert v-if="error.length" show variant="danger" qid="create-p-error">
        {{ error }}
      </b-alert>
      <b-form @submit="editObservationFormSubmit" qid="create-p-form">
        <div class="form-container">
          <b-form-group v-if="selectedTask.project && selectedTask.project.type != 'control_assessment'">
            <label qid="create-s-tags-label">
              {{ $t('projects.observations.create.scope_label') }}
            </label>
            <multiselect v-model="selectedScope" :options="scopeItems"
              :placeholder="$t('projects.observations.create.scope_placeholder')" label="name"
              :select-label="$t('system.dropdown_select_label')" :selected-label="$t('system.dropdown_selected_label')"
              :deselect-label="$t('system.dropdown_deselect_label')" qid="confidentiality-form-impact"></multiselect>
          </b-form-group>
          <b-form-group>
            <label qid="create-v-name-label">
              {{ $t('projects.observations.create.name_label') }}
              <span class="text-red">*</span>
            </label>
            <b-form-input qid="create-v-form-name" type="text" v-model="editForm.name" required :maxLength="255"
              :placeholder="$t('projects.observations.create.name_placeholder')"></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="create-v-description-label">
              {{ $t('projects.observations.create.description_label') }}
              <span class="text-red">*</span>
            </label>
            <b-form-textarea qid="create-v-form-description" v-model="editForm.description" rows="6" max-rows="6"
              required :placeholder="$t('projects.observations.create.description_placeholder')"></b-form-textarea>
          </b-form-group>
          <b-form-group>
            <label qid="create-s-tags-label">
              {{ $t('projects.observations.create.impact_label') }}
              <span class="text-red">*</span>
            </label>
            <multiselect v-model="selectedImpact" :options="impactItems"
              :placeholder="$t('projects.observations.create.impact_placeholder')" label="name"
              :select-label="$t('system.dropdown_select_label')" :selected-label="$t('system.dropdown_selected_label')"
              :deselect-label="$t('system.dropdown_deselect_label')" qid="confidentiality-form-impact"></multiselect>
          </b-form-group>
        </div>
        <div class="text-right">
          <b-button class="mt-3" variant="secondary" @click="hideEditModal" qid="create-p-cancel-button">
            {{ $t('projects.create.cancel') }}
          </b-button>
          <b-button type="submit" class="mt-3 ml-3" variant="success" :disabled="buttonDisabled"
            qid="create-p-submit-button">
            <span v-if="buttonDisabled" class="mr-1">
              <font-awesome-icon icon="spinner" size="sm" class="fa-spin" />
            </span>
            {{ $t('projects.create.submit') }}
          </b-button>
        </div>
      </b-form>

    </b-modal>



    <!-- Create test modal -->
    <b-modal ref="create-test" hide-footer modal-class="task-modal" @hide="hideEditModal">
      <template #modal-header="{ close }">
        <div class="" style="width: 100%;">
          <span class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times" />
          </span>
          <h5 class="mb-1 text-center" qid="update-user-title">
            {{ $t('testing.form.create_new_test') }}
          </h5>
        </div>
      </template>
      <b-alert v-if="error.length" show variant="danger" qid="create-p-error">
        {{ error }}
      </b-alert>
      <b-form @submit="createTestFormSubmit" qid="create-p-form">
        <div class="form-container">
          <b-form-group>
            <label qid="create-v-name-label">
              {{ $t('testing.form.title') }}
              <span class="text-red">*</span>
            </label>
            <b-form-input qid="create-v-form-name" type="text" v-model="testForm.name" required :maxLength="255"
              :placeholder="$t('testing.form.title')"></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="create-v-description-label">
              {{ $t('testing.form.description') }}
              <span class="text-red">*</span>
            </label>
            <b-form-textarea qid="create-v-form-description" v-model="testForm.description" rows="6" max-rows="6"
              required :placeholder="$t('testing.form.description')"></b-form-textarea>
          </b-form-group>
          <b-form-group>
            <label qid="create-q-pg-label">
              {{ $t('testing.form.allow_description') }}
              <span class="text-red">*</span>
            </label>
            <multiselect v-model="testForm.allow_description" :options="allowTypes"
              :placeholder="$t('testing.form.allow_description')" label="name"
              :select-label="$t('system.dropdown_select_label')" :selected-label="$t('system.dropdown_selected_label')"
              :deselect-label="$t('system.dropdown_deselect_label')" qid="create-q-form-pg"></multiselect>
          </b-form-group>

          <b-form-group>
            <label qid="create-q-pg-label">
              {{ $t('testing.form.allow_attachments') }}
              <span class="text-red">*</span>
            </label>
            <multiselect v-model="testForm.allow_attachment" :options="allowTypes"
              :placeholder="$t('testing.form.allow_attachments')" label="name"
              :select-label="$t('system.dropdown_select_label')" :selected-label="$t('system.dropdown_selected_label')"
              :deselect-label="$t('system.dropdown_deselect_label')" qid="create-q-form-pg"></multiselect>
          </b-form-group>
        </div>
        <div class="proxy-footer">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="row">
              <div class="col-sm-6">

              </div>
              <div class="col-sm-6">
                <div class="text-right">
                  <b-button class="mt-3" variant="secondary" @click="hideEditTestModal" qid="create-p-cancel-button">
                    {{ $t('projects.create.cancel') }}
                  </b-button>
                  <b-button type="submit" class="mt-3 ml-3" variant="success" :disabled="buttonDisabled"
                    qid="create-p-submit-button">
                    <span v-if="buttonDisabled" class="mr-1">
                      <font-awesome-icon icon="spinner" size="sm" class="fa-spin" />
                    </span>
                    {{ $t('projects.create.submit') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-form>

    </b-modal>


    <!-- Edit test modal -->
    <b-modal ref="update-test" hide-footer modal-class="task-modal" @hide="hideUpdateTestModal">
      <template #modal-header="{ close }">
        <div class="" style="width: 100%;">
          <span class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times" />
          </span>
          <h5 class="mb-1 text-center" qid="update-user-title">
            {{ $t('testing.form.update_test') }}
          </h5>
        </div>
      </template>
      <b-alert v-if="error.length" show variant="danger" qid="create-p-error">
        {{ error }}
      </b-alert>
      <b-form @submit="updateTestFormSubmit" qid="create-p-form">
        <div class="form-container">
          <b-form-group>
            <label qid="create-v-name-label">
              {{ $t('testing.form.title') }}
              <span class="text-red">*</span>
            </label>
            <b-form-input qid="create-v-form-name" type="text" v-model="testForm.name" required :maxLength="255"
              :placeholder="$t('testing.form.title')"></b-form-input>
          </b-form-group>
          <b-form-group>
            <label qid="create-v-description-label">
              {{ $t('testing.form.description') }}
              <span class="text-red">*</span>
            </label>
            <b-form-textarea qid="create-v-form-description" v-model="testForm.description" rows="6" max-rows="6"
              required :placeholder="$t('testing.form.description')"></b-form-textarea>
          </b-form-group>
          <b-form-group>
            <label qid="create-q-pg-label">
              {{ $t('testing.form.allow_description') }}
              <span class="text-red">*</span>
            </label>
            <multiselect v-model="testForm.allow_description" :options="allowTypes"
              :placeholder="$t('testing.form.allow_description')" label="name"
              :select-label="$t('system.dropdown_select_label')" :selected-label="$t('system.dropdown_selected_label')"
              :deselect-label="$t('system.dropdown_deselect_label')" qid="create-q-form-pg"></multiselect>
          </b-form-group>

          <b-form-group>
            <label qid="create-q-pg-label">
              {{ $t('testing.form.allow_attachments') }}
              <span class="text-red">*</span>
            </label>
            <multiselect v-model="testForm.allow_attachment" :options="allowTypes"
              :placeholder="$t('testing.form.allow_attachments')" label="name"
              :select-label="$t('system.dropdown_select_label')" :selected-label="$t('system.dropdown_selected_label')"
              :deselect-label="$t('system.dropdown_deselect_label')" qid="create-q-form-pg"></multiselect>
          </b-form-group>
        </div>
        <div class="proxy-footer">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="row">
              <div class="col-sm-6">

              </div>
              <div class="col-sm-6">
                <div class="text-right">
                  <b-button class="mt-3" variant="secondary" @click="hideUpdateTestModal" qid="create-p-cancel-button">
                    {{ $t('projects.create.cancel') }}
                  </b-button>
                  <b-button type="submit" class="mt-3 ml-3" variant="success" :disabled="buttonDisabled"
                    qid="create-p-submit-button">
                    <span v-if="buttonDisabled" class="mr-1">
                      <font-awesome-icon icon="spinner" size="sm" class="fa-spin" />
                    </span>
                    {{ $t('projects.create.submit') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>

      </b-form>

    </b-modal>


    <!-- Show test modal -->
    <b-modal ref="show-test" hide-footer :title="testForm.name" modal-class="task-modal" @hide="hideShowTestModal">
      <template #modal-header="{ close }">
        <div class="" style="width: 100%;">
          <span class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times" />
          </span>
          <h5 class="mb-1 text-center" qid="update-user-title">
            {{ selectedTask.reference }}: {{ selectedTest.name }}
          </h5>
          <div class="mb-2 mt-2">
            {{ testForm.description }}
          </div>
        </div>
      </template>
      <b-alert v-if="error.length" show variant="danger" qid="create-p-error">
        {{ error }}
      </b-alert>
      <b-form qid="create-p-form">

        <div class="form-container">
          <b-form-group v-if="isDataOwner(selectedTask) && selectedTest.allow_description != 'No'">
            <label qid="assessment-form-documentation-collection-control-description-label" style="font-size: medium;"
              class="mt-3">
              {{ $t('testing.form.description') }}
              <span class="text-red" v-if="selectedTask && selectedTest.allow_description == 'Mandatory'">*</span>
            </label>

            <span
              v-if="currentView != 'data_collection' || (currentView == 'data_collection' && selectedTest.status != 'passed' && selectedTest.status != 'failed')"
              class="btn btn-edit float-right"
              @click="editModeControlDescriptionDocumentationCollection = !editModeControlDescriptionDocumentationCollection">
              <font-awesome-icon icon="pen" />
            </span>

            <b-form-textarea qid="assessment-form-documentation-collection-control-description"
              v-model="testForm.description_data" v-if="editModeControlDescriptionDocumentationCollection" rows="6"
              max-rows="6" :placeholder="$t('tasks.modal.control_description_label')"></b-form-textarea>

            <div v-if="!editModeControlDescriptionDocumentationCollection" style="margin-top: -20px;">
              <div v-if="testForm.description_data && testForm.description_data.length > 300">
                <p v-if="showFullTextControlDescriptionDocumentationCollection" class="text-formatted">
                  {{ testForm.description_data }}
                </p>

                <p v-if="!showFullTextControlDescriptionDocumentationCollection" class="text-formatted">
                  {{ testForm.description_data | truncate(300) }}
                </p>

                <div class="text-center mt-3"
                  @click="showFullTextControlDescriptionDocumentationCollection = !showFullTextControlDescriptionDocumentationCollection">
                  <span class="cursor-pointer" v-if="!showFullTextControlDescriptionDocumentationCollection">
                    <span class="mr-2">
                      {{ $t('system.view_more') }}
                    </span>
                    <font-awesome-icon icon="chevron-down" />
                  </span>
                  <span class="cursor-pointer" v-if="showFullTextControlDescriptionDocumentationCollection">
                    <span class="mr-2">
                      {{ $t('system.view_less') }}
                    </span>
                    <font-awesome-icon icon="chevron-up" />
                  </span>
                </div>
              </div>

              <p v-if="testForm.description_data && testForm.description_data.length <= 300" class="text-formatted">
                {{ testForm.description_data }}
              </p>
            </div>
          </b-form-group>


          <div v-if="selectedTest.allow_attachment != 'No'" class="w-100" @dragover.prevent>
            <div v-show="showDropActive" class="drop-active">
              <h3>Drop files to upload</h3>
            </div>

            <label qid="assessment-form-conclusion-label" style="font-size: medium;" class="mt-3">
              {{ $t('attachments.label') }}
              <span class="text-red" v-if="selectedTask && selectedTest.allow_attachment == 'Mandatory'">*</span>
            </label>
            <span class="float-right"
              v-if="currentView != 'data_collection' || (currentView == 'data_collection' && selectedTest.status != 'passed' && selectedTest.status != 'failed')">
              <file-upload ref="uploader" v-model="test_files" :drop="true" :multiple="true" input-id="test-file"
                @input="onTestFileChange" @dropActive="showDropActiveFunction" @dropInactive="hideDropActiveFunction"
                class="text-success cursor-pointer float:right mt-3">
                {{ $t('tasks.modal.add_attachment_label') }}
              </file-upload>
            </span>
          </div>
          <ul class="compact-list" style="list-style-type: none; padding-left: 0;"
            v-if="selectedTest && selectedTest.allow_attachment != 'No'">
            <li v-for="(item, index) in getTestMedia('documents')" :key="index" class="text-bold list-item">
              <div class="row">
                <div class="col-md-8" style="line-height: 20px;">
                  <a :href="item.url" target="_blank" class="text-success" qid="download-media">
                    {{ item.full_title }}
                  </a>
                </div>
                <div class="col-md-4 actions" style="text-align: right;">
                  <a :href="item.url" target="_blank" class="btn" qid="download-media">
                    <font-awesome-icon icon="eye" />
                  </a>
                  <a class="btn" @click="downloadMedia(item)" qid="download-media">
                    <font-awesome-icon icon="cloud-download-alt" />
                  </a>
                  <a class="btn" @click="openDeleteAttachmentModal(item)" qid="remove-media-item">
                    <font-awesome-icon icon="trash-alt" />
                  </a>
                </div>
              </div>
            </li>
            <li v-if="!getTestMedia('documents').length" qid="no-results-row-rr">
              <div class="text-muted">{{ $t('system.no_results') }}</div>
            </li>
          </ul>

          <b-form-group v-if="isOwner(selectedTask) && currentView != 'data_collection'">
            <label qid="create-q-pg-label">
              {{ $t('testing.form.status') }}
            </label>
            <multiselect v-model="testForm.status" :options="testStatus" :placeholder="$t('testing.form.status')"
              label="name" :select-label="$t('system.dropdown_select_label')"
              :selected-label="$t('system.dropdown_selected_label')"
              :deselect-label="$t('system.dropdown_deselect_label')" :allow-empty="false" :multiple="false"
              :taggable="false" qid="OE testing status">
              <template slot="singleLabel" slot-scope="props">
                <font-awesome-icon icon="exclamation-triangle" :class="'risk-9'"
                  v-if="props.option.value == 'pending'" />
                <font-awesome-icon icon="info-circle" class="icon-info"
                  v-if="props.option.value == 'documentation_collection_completed'"
                  style="background:white;border-radius:50%;" />
                <font-awesome-icon icon="times-circle" class="icon-danger" v-if="props.option.value == 'failed'"
                  style="background:white;border-radius:50%;" />
                <font-awesome-icon icon="check-circle" class="icon-success" v-if="props.option.value == 'passed'"
                  style="background:white;border-radius:50%;" />
                <span class="ml-2">
                  {{ props.option.name }}
                </span>
              </template>
              <template slot="option" slot-scope="props">
                <font-awesome-icon icon="exclamation-triangle" :class="'risk-9'"
                  v-if="props.option.value == 'pending'" />
                <font-awesome-icon icon="info-circle" class="icon-info"
                  v-if="props.option.value == 'documentation_collection_completed'"
                  style="background:white;border-radius:50%;" />
                <font-awesome-icon icon="times-circle" class="icon-danger" v-if="props.option.value == 'failed'"
                  style="background:white;border-radius:50%;" />
                <font-awesome-icon icon="check-circle" class="icon-success" v-if="props.option.value == 'passed'"
                  style="background:white;border-radius:50%;" />
                <span class="ml-2">
                  {{ props.option.name }}
                </span>
                <span v-if="props.option.$groupLabel">
                  {{ props.option.$groupLabel }}
                </span>
              </template>
            </multiselect>

          </b-form-group>

          <b-form-group v-if="isOwner(selectedTask) && currentView != 'data_collection'">
            <label qid="create-v-description-label">
              {{ $t('testing.form.comment') }}
            </label>
            <b-form-textarea qid="create-v-form-description" v-model="testForm.comment" rows="6" max-rows="6"
              :placeholder="$t('testing.form.description')"></b-form-textarea>
          </b-form-group>
        </div>
        <div class="proxy-footer">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="row">
              <div class="col-sm-12">
                <div class="text-right">
                  <b-button class="mt-3" variant="secondary" @click="hideShowTestModal"
                    v-if="currentView != 'data_collection'" qid="create-p-cancel-button">
                    {{ $t('projects.create.cancel') }}
                  </b-button>
                  <b-button type="submit" class="mt-3 ml-3" variant="success" :disabled="buttonDisabled"
                    qid="create-p-submit-button" v-if="currentView != 'data_collection'" @click="saveWithoutComplete">
                    <span v-if="buttonDisabled" class="mr-1">
                      <font-awesome-icon icon="spinner" size="sm" class="fa-spin" />
                    </span>
                    {{ $t('projects.create.submit') }}
                  </b-button>
                  <b-button type="submit" class="mt-3 ml-3" variant="secondary" :disabled="buttonDisabled"
                    qid="create-p-submit-button"
                    v-if="currentView == 'data_collection' && selectedTest.status != 'passed' && selectedTest.status != 'failed'"
                    @click="saveWithoutComplete">
                    <span v-if="buttonDisabled" class="mr-1">
                      <font-awesome-icon icon="spinner" size="sm" class="fa-spin" />
                    </span>
                    {{ $t('system.save_without_complete') }}
                  </b-button>
                  <b-button type="submit" class="mt-3 ml-3" variant="success" :disabled="buttonDisabled"
                    qid="create-p-submit-button"
                    v-if="currentView == 'data_collection' && selectedTest.status != 'passed' && selectedTest.status != 'failed'"
                    @click="completeSubmission">
                    <span v-if="buttonDisabled" class="mr-1">
                      <font-awesome-icon icon="spinner" size="sm" class="fa-spin" />
                    </span>
                    {{ $t('tasks.modal.complete') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>

      </b-form>

    </b-modal>

  </span>

  </div>
</template>

<script>
import assessmentSingleTaskProxy from './assessmentSingleTaskProxy';
import Datepicker from 'vuejs-datepicker';
import Avatar from '@/components/Avatar.vue';
import ConfirmRemoveAttachment from '@/components/ConfirmRemoveAttachment.vue';
import GuideViewModal from '@/components/guide/GuideViewModal.vue';
import ShowMoreModal from '@/components/ShowMoreModal.vue';
import UserInviteModal from '@/components/user/UserInviteModal.vue';
import AssessmentGlobalSingleTaskModalEditQuestionWizard from '@/views/Tasks/Assessment/AssessmentGlobalSingleTaskModalEditQuestionWizard.vue';

export default {
  name: 'AssessmentGlobalSingleTaskModalAuxModals',
  mixins: [assessmentSingleTaskProxy],
  components: {
    Datepicker,
    Avatar,
    ConfirmRemoveAttachment,
    GuideViewModal,
    ShowMoreModal,
    UserInviteModal,
    AssessmentGlobalSingleTaskModalEditQuestionWizard
  }
};
</script>
